import { supabase } from './client.js';

/**
 * 用户认证服务
 * 提供登录、注册、登出等认证功能
 */

// 用户登录
export const signIn = async (email, password) => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true, user: data.user };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 用户注册
export const signUp = async (email, password, userData = {}) => {
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: userData, // 额外的用户数据
      },
    });

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true, user: data.user };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 用户登出
export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut();

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 获取当前用户
export const getCurrentUser = async () => {
  try {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true, user };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 获取用户会话
export const getSession = async () => {
  try {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true, session };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 监听认证状态变化
export const onAuthStateChange = callback => {
  return supabase.auth.onAuthStateChange(callback);
};

// 重置密码
export const resetPassword = async email => {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email);

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 更新用户密码
export const updatePassword = async newPassword => {
  try {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 发送 OTP 验证码
export const sendOTP = async email => {
  try {
    // 使用 supabase.functions.invoke 调用 Edge Function
    const { data, error } = await supabase.functions.invoke(
      'send-verification-code',
      {
        body: { email },
      }
    );

    // 当返回 429 状态码时，error 会被设置，但 data 可能包含响应体
    // 需要同时检查 error 和 data
    if (error) {
      console.log('[sendOTP] Error response:', { error, data });

      // 尝试从 data 中获取冷却时间信息（429 响应）
      // 429 响应通常会在 data 中包含响应体
      if (data && (data.reason === 'cooldown' || data.ok === false)) {
        const remaining = data.remaining || 0;
        if (remaining > 0) {
          const minutes = Math.ceil(remaining / 60);
          const seconds = remaining % 60;
          return {
            success: false,
            error: `Please wait ${minutes} minute${minutes > 1 ? 's' : ''}${
              seconds > 0
                ? ` and ${seconds} second${seconds > 1 ? 's' : ''}`
                : ''
            } before requesting another code`,
            remaining: remaining, // 返回剩余秒数，供前端显示倒计时
          };
        }
      }

      // 尝试从 error.context 或其他地方获取响应数据
      // Supabase 客户端可能将响应体放在 error.context 中
      if (error.context && error.context.body) {
        try {
          const errorBody =
            typeof error.context.body === 'string'
              ? JSON.parse(error.context.body)
              : error.context.body;
          if (errorBody.reason === 'cooldown' && errorBody.remaining) {
            const remaining = errorBody.remaining;
            const minutes = Math.ceil(remaining / 60);
            const seconds = remaining % 60;
            return {
              success: false,
              error: `Please wait ${minutes} minute${minutes > 1 ? 's' : ''}${
                seconds > 0
                  ? ` and ${seconds} second${seconds > 1 ? 's' : ''}`
                  : ''
              } before requesting another code`,
              remaining: remaining,
            };
          }
        } catch (e) {
          console.error('[sendOTP] Failed to parse error context:', e);
        }
      }

      // 其他错误情况
      const errorMessage =
        data?.error || error.message || 'Failed to send verification code';
      return {
        success: false,
        error: errorMessage,
      };
    }

    // 检查 data 中的 ok 字段（Edge Function 返回的格式）
    if (data && data.ok === false) {
      // 处理冷却时间等特殊情况
      if (data.reason === 'cooldown') {
        const remaining = data.remaining || 0;
        const minutes = Math.ceil(remaining / 60);
        const seconds = remaining % 60;
        return {
          success: false,
          error: `Please wait ${minutes} minute${minutes > 1 ? 's' : ''}${
            seconds > 0 ? ` and ${seconds} second${seconds > 1 ? 's' : ''}` : ''
          } before requesting another code`,
          remaining: remaining,
        };
      }
      return {
        success: false,
        error: data.error || 'Failed to send verification code',
      };
    }

    return { success: true, data };
  } catch (error) {
    console.error('[sendOTP] Error:', error);
    return {
      success: false,
      error: error.message || 'Failed to send verification code',
    };
  }
};

// 验证 OTP
export const verifyOTP = async (email, token) => {
  try {
    // 验证参数
    if (!email || !token) {
      console.error('[verifyOTP] Missing parameters:', { email, token });
      return {
        success: false,
        error: 'Email and verification code are required',
      };
    }

    const requestBody = {
      email: email.trim(),
      code: token.trim(),
    };

    console.log('[verifyOTP] Calling verify-code-only with:', {
      email: requestBody.email,
      codeLength: requestBody.code.length,
    });

    // 使用 supabase.functions.invoke 调用 Edge Function
    const { data, error } = await supabase.functions.invoke(
      'verify-code-only',
      {
        body: requestBody,
      }
    );

    console.log('[verifyOTP] Response:', { data, error });

    // 详细记录错误信息以便调试
    if (error) {
      console.log('[verifyOTP] Error details:', {
        errorType: error?.constructor?.name,
        errorMessage: error?.message,
        errorContext: error?.context,
        errorStatus: error?.status,
        errorFull: error,
        errorKeys: Object.keys(error || {}),
        contextType: error?.context?.constructor?.name,
        contextKeys: error?.context ? Object.keys(error.context) : null,
      });

      // 如果 error.context 是 Response 对象，尝试读取响应体
      if (error.context instanceof Response) {
        try {
          // 尝试克隆 Response 对象（如果 body 已被读取）
          const clonedResponse = error.context.clone();
          const responseBody = await clonedResponse.json();
          console.log(
            '[verifyOTP] Response body from cloned Response:',
            responseBody
          );
        } catch (e) {
          console.log('[verifyOTP] Cannot clone/read Response:', e);
          // 尝试直接读取（如果还没被读取）
          try {
            const responseBody = await error.context.json();
            console.log(
              '[verifyOTP] Response body from original Response:',
              responseBody
            );
          } catch (e2) {
            console.log('[verifyOTP] Cannot read Response body:', e2);
          }
        }
      }
    }

    // 检查是否有错误
    if (error) {
      let errorMessage = 'Failed to verify code';

      // 方法1: 尝试从 data 中获取错误信息
      if (data && data.error) {
        errorMessage = data.error;
      } else if (data && data.ok === false && data.error) {
        errorMessage = data.error;
      }

      // 方法2: 尝试从 error.context 获取响应体
      // error.context 可能是 Response 对象，需要异步读取
      if (!errorMessage || errorMessage === 'Failed to verify code') {
        if (error.context) {
          // 如果 error.context 是 Response 对象，需要读取响应体
          if (
            error.context instanceof Response ||
            typeof error.context.json === 'function'
          ) {
            try {
              // 尝试从 Response 对象读取 JSON（异步）
              const responseBody = await error.context.json();
              console.log('[verifyOTP] Parsed response body:', responseBody);
              if (responseBody.error) {
                errorMessage = responseBody.error;
                // 如果错误信息提到 password，说明可能调用了错误的 Edge Function
                if (errorMessage.includes('password')) {
                  console.error(
                    '[verifyOTP] ERROR: Edge Function returned password-related error. This suggests verify-code-only may be incorrectly routed to verify-code function.'
                  );
                  errorMessage =
                    'Verification code is invalid or has expired. Please check your code or request a new one.';
                }
              } else if (responseBody.ok === false && responseBody.error) {
                errorMessage = responseBody.error;
              }
            } catch (e) {
              // 如果 json() 失败，尝试 text()
              try {
                const responseText = await error.context.text();
                const responseBody = JSON.parse(responseText);
                console.log('[verifyOTP] Parsed response text:', responseBody);
                if (responseBody.error) {
                  errorMessage = responseBody.error;
                } else if (responseBody.ok === false && responseBody.error) {
                  errorMessage = responseBody.error;
                }
              } catch (e2) {
                console.error(
                  '[verifyOTP] Failed to parse error.context (Response):',
                  e2
                );
              }
            }
          }
          // 检查 error.context.data（某些版本的 Supabase 客户端）
          else if (error.context.data) {
            try {
              const errorData =
                typeof error.context.data === 'string'
                  ? JSON.parse(error.context.data)
                  : error.context.data;
              if (errorData.error) {
                errorMessage = errorData.error;
              } else if (errorData.ok === false && errorData.error) {
                errorMessage = errorData.error;
              }
            } catch (e) {
              console.error(
                '[verifyOTP] Failed to parse error.context.data:',
                e
              );
            }
          }
          // 检查 error.context.body
          else if (error.context.body) {
            try {
              const errorBody =
                typeof error.context.body === 'string'
                  ? JSON.parse(error.context.body)
                  : error.context.body;
              if (errorBody.error) {
                errorMessage = errorBody.error;
              } else if (errorBody.ok === false && errorBody.error) {
                errorMessage = errorBody.error;
              }
            } catch (e) {
              console.error(
                '[verifyOTP] Failed to parse error.context.body:',
                e
              );
            }
          }
        }

        // 方法3: 尝试从 error 对象本身获取（某些情况下错误信息可能在 error 的属性中）
        if (
          (!errorMessage || errorMessage === 'Failed to verify code') &&
          error.data
        ) {
          try {
            const errorData =
              typeof error.data === 'string'
                ? JSON.parse(error.data)
                : error.data;
            if (errorData.error) {
              errorMessage = errorData.error;
            } else if (errorData.ok === false && errorData.error) {
              errorMessage = errorData.error;
            }
          } catch (e) {
            console.error('[verifyOTP] Failed to parse error.data:', e);
          }
        }
      }

      // 方法3: 尝试从 error.message 获取（如果包含 JSON）
      if (!errorMessage || errorMessage === 'Failed to verify code') {
        if (error.message) {
          // 如果 error.message 包含 "Edge Function returned" 等，尝试提取实际错误
          if (
            error.message.includes('non-2xx') ||
            error.message.includes('Edge Function')
          ) {
            // 这是 Supabase 的通用错误消息，需要从其他地方获取实际错误
            errorMessage = 'Verification code is invalid or has expired';
          } else {
            errorMessage = error.message;
          }
        }
      }

      // 如果仍然没有具体错误信息，使用默认消息
      if (!errorMessage || errorMessage === 'Failed to verify code') {
        // 根据状态码推断错误类型
        if (error.status === 400) {
          errorMessage =
            'Verification code is invalid or has expired. Please check your code or request a new one.';
        } else {
          errorMessage = 'Failed to verify code. Please try again.';
        }
      }

      return {
        success: false,
        error: errorMessage,
      };
    }

    // 检查 data 中的 ok 字段（Edge Function 返回的格式）
    if (data && data.ok === false) {
      return {
        success: false,
        error: data.error || 'Verification code error or expired',
      };
    }

    // OTP 驗證成功，返回成功狀態
    return {
      success: true,
      user: { email: data?.email || email }, // 暫時返回基本信息
      session: null, // 還沒有會話
    };
  } catch (error) {
    console.error('[verifyOTP] Error:', error);
    return {
      success: false,
      error: error.message || 'Failed to verify code',
    };
  }
};

// 设置用户密码
export const setUserPassword = async password => {
  try {
    const { data, error } = await supabase.auth.updateUser({
      password: password,
    });

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true, user: data.user };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 創建用戶並設置密碼（使用我們的 Edge Function）
export const createUserWithPassword = async (email, code, password) => {
  try {
    // 使用 supabase.functions.invoke 调用 Edge Function
    const { data, error } = await supabase.functions.invoke('verify-code', {
      body: {
        email,
        code,
        password,
      },
    });

    if (error) {
      return {
        success: false,
        error: error.message || 'Failed to create user',
      };
    }

    // 用戶創建成功，返回用戶信息和會話
    return {
      success: true,
      user: data.user,
      session: data.session,
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 通過驗證碼重置密碼（使用我們的 Edge Function）
export const resetPasswordWithCode = async (email, code, newPassword) => {
  try {
    console.log('[resetPasswordWithCode] Calling reset-password with:', {
      email,
      codeLength: code?.length,
    });

    // 使用 supabase.functions.invoke 调用 Edge Function
    const { data, error } = await supabase.functions.invoke('reset-password', {
      body: {
        email,
        code,
        password: newPassword,
      },
    });

    console.log('[resetPasswordWithCode] Response:', { data, error });

    if (error) {
      // 检查是否是 404 错误（Edge Function 未部署）
      if (error.message?.includes('404') || error.status === 404) {
        return {
          success: false,
          error:
            'Reset password function is not available. Please contact support.',
        };
      }

      // 尝试从 error.context 获取响应体
      let errorMessage = error.message || 'Failed to reset password';
      if (error.context instanceof Response) {
        try {
          const responseBody = await error.context.json();
          if (responseBody.error) {
            errorMessage = responseBody.error;
          }
        } catch (e) {
          console.error(
            '[resetPasswordWithCode] Failed to parse error context:',
            e
          );
        }
      }

      return {
        success: false,
        error: errorMessage,
      };
    }

    // 檢查 Edge Function 返回的結果
    if (data && !data.ok) {
      let errorMessage = data.error || 'Failed to reset password';

      // 简化 Supabase 密码策略错误提示
      if (
        errorMessage.includes('Password should contain') ||
        errorMessage.includes('abcdefghijklmnopqrstuvwxyz') ||
        errorMessage.includes('uppercase') ||
        errorMessage.includes('lowercase') ||
        errorMessage.includes('numbers') ||
        errorMessage.includes('digits')
      ) {
        // 简化错误提示，与前端显示的要求一致
        errorMessage =
          'Password must be at least 6 characters and include uppercase, lowercase, and a number.';
      }

      return {
        success: false,
        error: errorMessage,
      };
    }

    return {
      success: true,
      message: data?.message || 'Password reset successfully',
    };
  } catch (error) {
    console.error('[resetPasswordWithCode] Error:', error);
    return {
      success: false,
      error: error.message || 'Failed to reset password',
    };
  }
};
